import "./Home.scss";

import { ActionIcon, Button, Divider, Loader, Text } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetApi, useIsOnline } from "src/hooks";
import { Flex, PageContainer } from "src/styled";
import { titlecase } from "src/utils";

import BackToYachtButton from "../../components/shared/BackToYachtButton/BackToYachtButton";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import useManagementLinks from "../../components/shared/Breadcrumbs/useManagementLinks";
import { useDispatch } from "../../redux/hooks";
import { setManagementLinks } from "../../redux/slices/permsSlice";
import {
  ButtonsContainer,
  HomeDividerText,
  HomeHeader,
  HomeHeaderWrapper,
  HomeLogbooks,
} from "./Home.styled";
import { type TLogbookStatus } from "./Home.types";
import { groupLogbooks } from "./Home.utils";
import StatusButton from "../../components/pages/Home/StatusButton";
import LogbooksList from "../../components/pages/Home/LogbooksList";
import SearchInput from "src/components/shared/LocalSearch/SearchInput";
import { IconSearch } from "@tabler/icons-react";
import { useIsDev } from "src/hooks/useIsDev";

function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isOnline = useIsOnline();
  const { isAuthorizedToSeeLinks, managementLinks } = useManagementLinks();

  const lbtype = searchParams.get(`lbtype`) as TLogbookStatus;
  const [status, setStatus] = useState<TLogbookStatus>(lbtype || "active");

  const [search, setSearch] = useState("");
  const isDev = useIsDev();

  const mapper = useCallback((data: any) => {
    if (
      data?.data?.logbooks?.customDashboard &&
      typeof data?.data?.logbooks?.customDashboard === "object"
    ) {
      const SPECIAL_COLLECTIONS = [
        "8c1b02d8-3f89-11f0-8c47-0a900ab7ef27",
        "b8bb6278-5cbd-11f0-8651-02c1fe2db5a5",
      ];
      data = groupLogbooks(data);
      const customDashboardData = data?.active[0]?.logbooks?.filter(
        lb =>
          lb.lbtype === "collection" && SPECIAL_COLLECTIONS.includes(lb.lbid),
      );
      customDashboardData &&
        customDashboardData.length > 0 &&
        navigate(
          `/collection/${customDashboardData[0]?.lbid}/custom-dashboard`,
        );
    } else {
      return groupLogbooks(data);
    }
  }, []);

  const { data, isLoading } = useGetApi({
    request: "get-home",
    mapper,
  });

  useEffect(() => {
    if ((data as any)?.ids && isAuthorizedToSeeLinks) {
      dispatch(setManagementLinks((data as any).ids));
    }
  }, [dispatch, data, isAuthorizedToSeeLinks]);

  useEffect(() => {
    isDev && data && console.log(data);
  }, [data]);

  return (
    <PageContainer>
      <HomeHeaderWrapper>
        {isAuthorizedToSeeLinks && managementLinks && (
          <Flex align="center">
            <Breadcrumbs customLinks={managementLinks} border={false} />
            <BackToYachtButton />
          </Flex>
        )}
        <HomeHeader>
          <ButtonsContainer>
            <StatusButton name="active" status={status} setStatus={setStatus} />
            <StatusButton
              name="expired"
              status={status}
              setStatus={setStatus}
            />
            <StatusButton
              name="available"
              status={status}
              setStatus={setStatus}
            />
          </ButtonsContainer>
          <ButtonsContainer>
            {/* {isDev && (
              <Button
                onClick={() => navigate("/dev/generator-v2")}
                size="sm"
                variant="outline"
                color="cyan">
                Generator V2
              </Button>
            )} */}
            <Button
              onClick={() => navigate("/summary")}
              size="sm"
              variant="outline">
              Summary
            </Button>
            <Button
              onClick={() => navigate("/reports")}
              size="sm"
              variant="outline">
              Reports
            </Button>
            <Button
              onClick={() => navigate("/organize-logbooks")}
              size="sm"
              variant="outline"
              disabled={!isOnline}>
              Organize
            </Button>
            {/* <Button */}
            {/*  onClick={() => navigate("/settings/miscellaneous/labels")} */}
            {/*  size="sm" */}
            {/*  variant="outline" */}
            {/*  disabled={!isOnline}> */}
            {/*  Labels */}
            {/* </Button> */}
          </ButtonsContainer>
        </HomeHeader>
      </HomeHeaderWrapper>
      <Divider mt={24} />
      <HomeDividerText data-cy="selected-logbook-status">
        <Flex justify="space-between" align="center">
          {status === "active" ? "Subscribed" : titlecase(status)} Logbooks
          <SearchInput
            containerStyles={{ marginRight: 0 }}
            value={search}
            onChange={setSearch}
            icon={
              <ActionIcon variant="transparent">
                <IconSearch size={20} />
              </ActionIcon>
            }
            label="Search logbooks..."
          />
        </Flex>
      </HomeDividerText>
      <Divider />

      <HomeLogbooks>
        {!data || isLoading ? (
          <Loader />
        ) : (
          <LogbooksList data={data} status={status} search={search} />
        )}
      </HomeLogbooks>
    </PageContainer>
  );
}

export default Home;
